import { logErrorOnServer } from '../../../../../helpers/serverLogging'
import { savePendingRequest } from '../../../../../../../indexedDb/pendingRequests'
import { handleOnCancel, handleOnDone } from '../../../../../utils/handleOnDone'
import {
    trackRepairRequestCreatedOnline,
    trackEstimateRequestCreated,
} from '../../../../../utils/sendGAAnalyticsEvent'
const logError = logErrorOnServer('New Repair Request')

export const createRequestHandler = ({
    setSuccessfulSubmit,
    setError,
    changeLoading,
    setAfterHoursModalOpen,
    changeNeedsUpdate,
    sendEventToIntercom,
    handleChangeAlert,
    handleChangeOpen,
    history,
    processImagesHelper,
    handleCreateServiceRequest,
    setImageRetryAttempted,
}) => {
    const handleCommonSuccess = () => {
        setSuccessfulSubmit(true)
        setError(null)
        changeLoading(false)
        setAfterHoursModalOpen(false)
        changeNeedsUpdate({ dashboard: true, request: true })
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'serviceRequestFormSubmission' })
    }

    const onCreatedRequestSuccess = (response, isEstimate) => {
        const requestId = response?.data?.response?.salesforceId
        const createdWithoutImage = response?.data?.createdWithoutImage

        handleCommonSuccess()
        setImageRetryAttempted(false)
        sendEventToIntercom('successful-repair-request')

        const alertOptions = {
            title: isEstimate
                ? 'Your request has been sent'
                : 'Your Repair Request has been sent',
            message: createdWithoutImage
                ? 'Your request was created, but the image could not be processed and was not included.'
                : isEstimate
                ? 'Check back to follow the status of your estimate'
                : 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                handleOnDone(history, requestId, handleChangeOpen, 'Repair')
            },
            type: 'success',
        }

        handleChangeAlert(alertOptions)
    }

    const onCreatedOfflineRequestSuccess = request => {
        handleCommonSuccess()

        handleChangeAlert({
            title: 'Your request is now pending',
            message:
                'Please reconnect this device within 72 hours to avoid delays in processing your request.',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            textOk: 'View Request',
            onDone: () => {
                handleOnDone(
                    history,
                    request?.id,
                    handleChangeOpen,
                    request?.showType,
                )
            },
            type: 'question',
        })
    }

    const onCreatedRequestError = error => {
        const imageFailed = error?.response?.data?.imageFailed
        const imageRetryAttempted = error?.response?.data?.imageRetryAttempted
        const message =
            error?.response?.data?.message ||
            'Please check your internet connection and try again.'

        if (imageFailed && imageRetryAttempted) {
            setImageRetryAttempted(true)
        }

        setError(message)
        changeLoading(false)
        setAfterHoursModalOpen(false)
        logError(message)
    }

    const handleRequest = async ({
        afterHoursApproval,
        formValues,
        assetInfo,
        images,
        photosIds,
        user,
        isOffline,
        isEstimate,
        imageRetryAttempted,
        data,
    }) => {
        const approvalAfterHours =
            afterHoursApproval === null
                ? 'n/a'
                : afterHoursApproval === true
                ? 'approved'
                : 'declined'
        const formToUse = data || formValues
        const equipmentId = assetInfo?.external_id || assetInfo?.salesforceID
        const shipToAccountId = assetInfo?.shipto
            ? assetInfo?.shipto?.external_id
            : assetInfo?.shipTo
            ? assetInfo?.shipTo?.salesforceID
            : assetInfo?.shipToAccountId

        changeLoading(true)

        const details = {
            equipmentSFId: equipmentId,
            equipmentName: assetInfo.customerAlias || assetInfo.name,
            make: assetInfo?.manufacturer,
            model: assetInfo?.model,
            serviceLocationId:
                assetInfo?.serviceLocation?.external_id ||
                assetInfo?.serviceLocationId,
            shipToAccountId: shipToAccountId,
            openRequests: assetInfo?.openRequests,
            userId: user?.id,
            requestedBySFId: user?.external_contact_id,
            soldToAccountId: user?.company?.external_id,
        }

        if (isOffline || assetInfo.isCreatedOffline) {
            Object.assign(details, {
                customerAlias: assetInfo.customerAlias || assetInfo.name,
                requestedByName: `${user?.first_name ?? ''} ${user?.last_name ??
                    ''}`,
                address: assetInfo?.address || assetInfo.shippingAddress,
                locationName: assetInfo?.location || assetInfo?.shippingName,
                showType: 'Repair',
                type: 'Repair',
            })
        }

        const processedImages = isOffline
            ? await processImagesHelper(images)
            : images

        const requestInfo = {
            afterHoursApproval: approvalAfterHours,
            images: processedImages,
            photoIds: photosIds,
            isEstimate,
            imageRetryAttempted,
            ...details,
            ...formToUse,
        }

        if (isOffline) {
            try {
                const savedRequest = await savePendingRequest(requestInfo)
                onCreatedOfflineRequestSuccess(savedRequest)
            } catch (error) {
                setError('Failed to save request for offline processing')
                changeLoading(false)
                setAfterHoursModalOpen(false)
            }
            return
        }

        try {
            await handleCreateServiceRequest(
                requestInfo,
                response => {
                    if (isEstimate) {
                        trackEstimateRequestCreated(
                            response,
                            user?.external_contact_id,
                        )
                    } else {
                        trackRepairRequestCreatedOnline(
                            response,
                            user?.external_contact_id,
                        )
                    }
                    onCreatedRequestSuccess(response, isEstimate)
                },
                onCreatedRequestError,
            )
        } catch (error) {
            setError('Failed to submit request. Try again later.')
            changeLoading(false)
            setAfterHoursModalOpen(false)
        }
    }

    return { handleRequest }
}
